import { useState, useEffect, useCallback } from 'react';
import { menuService, Menu, MenuFilters, CreateMenuDto, UpdateMenuDto } from '../services/menu.service';

interface UseMenusReturn {
  menus: Menu[];
  loading: boolean;
  error: Error | null;
  meta: { page: number; limit: number; total: number; totalPages: number };
  filters: MenuFilters;
  setFilters: (filters: MenuFilters) => void;
  refetch: () => void;
  createMenu: (data: CreateMenuDto) => Promise<Menu>;
  updateMenu: (id: string, data: UpdateMenuDto) => Promise<Menu>;
  deleteMenus: (ids: string[]) => Promise<void>;
}

export const useMenus = (initialFilters: MenuFilters = {}): UseMenusReturn => {
  const [menus, setMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<MenuFilters>({
    page: 1,
    limit: 10,
    ...initialFilters,
  });
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await menuService.getMenus(filters);
      setMenus(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch menus'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  const createMenu = async (data: CreateMenuDto): Promise<Menu> => {
    const menu = await menuService.createMenu(data);
    await fetchMenus();
    return menu;
  };

  const updateMenu = async (id: string, data: UpdateMenuDto): Promise<Menu> => {
    const menu = await menuService.updateMenu(id, data);
    await fetchMenus();
    return menu;
  };

  const deleteMenus = async (ids: string[]): Promise<void> => {
    await menuService.deleteMenus(ids);
    await fetchMenus();
  };

  return {
    menus,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchMenus,
    createMenu,
    updateMenu,
    deleteMenus,
  };
};

export interface UseMenuDetailReturn {
  menu: Menu | null;
  loading: boolean;
  error: Error | null;
}

export const useMenuDetail = (id: string | undefined): UseMenuDetailReturn => {
  const [menu, setMenu] = useState<Menu | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    menuService
      .getMenuById(id)
      .then(setMenu)
      .catch((err) => setError(err instanceof Error ? err : new Error('Failed to fetch menu')))
      .finally(() => setLoading(false));
  }, [id]);

  return { menu, loading, error };
};
